import React from 'react';
import { Box, Typography } from '@mui/material';
import pdfMake from './pdfmakeClient';

export type TParty = {
    name: string;
    inn?: string;
    address?: string;
    phone?: string;
};

export type TBankBlock = {
    beneficiary: string;
    inn: string;
    bank: string;
    iik: string;
    kbe?: string;
    bik: string;
    knp?: string;
};

export type TItem = {
    code?: string;
    title: string;
    qty: number;
    unit: string;
    price: number;
    total: number;
};

interface IExcelLikeInvoice {
    number: string;
    date: string;
    warning_note?: string;
    bank_block: TBankBlock;
    supplier: TParty;
    customer: TParty;
    items: TItem[];
    totals: {
        subtotal: number;
        vat_included?: number;
        grand_total: number;
        grand_total_words?: string;
    };
};

interface IInvoiceProps {
    data: IExcelLikeInvoice;
    viewerHeight?: number;
}

const fmtKZT = (n: number) =>
    new Intl.NumberFormat('ru-KZ', { maximumFractionDigits: 0 }).format(n);

const thinGray = {
    hLineWidth: () => 0.5,
    vLineWidth: () => 0.5,
    hLineColor: () => '#bcbcbc',
    vLineColor: () => '#bcbcbc',
    paddingLeft: () => 6,
    paddingRight: () => 6,
    paddingTop: () => 5,
    paddingBottom: () => 5,
};

const lightRows = {
    hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length ? 0.5 : 0.5),
    vLineWidth: () => 0.5,
    hLineColor: () => '#bcbcbc',
    vLineColor: () => '#bcbcbc',
    paddingLeft: () => 6,
    paddingRight: () => 6,
    paddingTop: () => 4,
    paddingBottom: () => 4,
};

const borderWhiteRows = {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    hLineColor: () => '#ffffff',
    vLineColor: () => '#ffffff',
    paddingLeft: () => 0,
    paddingRight: () => 0,
    paddingTop: () => 6,
    paddingBottom: () => 6,
};

function composePartyLine(p: TParty) {
    const arr: string[] = [];
    if (p.inn) arr.push(`БИН / ИИН ${p.inn}`);
    arr.push(p.name);
    if (p.address) arr.push(`\n${p.address}`);
    if (p.phone) arr.push(`\nтел.: ${p.phone}`);
    return arr.join(', ');
}

function buildDocDefinition(d: IExcelLikeInvoice) {
    const bank: TBankBlock = {
        beneficiary: d.bank_block?.beneficiary ?? '',
        inn: d.bank_block?.inn ?? '',
        bank: d.bank_block?.bank ?? '',
        iik: d.bank_block?.iik ?? '',
        kbe: d.bank_block?.kbe ?? '',
        bik: d.bank_block?.bik ?? '',
        knp: d.bank_block?.knp ?? '',
    };
    const supplier: TParty = { name: d.supplier?.name ?? '', inn: d.supplier?.inn, address: d.supplier?.address, phone: d.supplier?.phone };
    const customer: TParty = { name: d.customer?.name ?? '', inn: d.customer?.inn, address: d.customer?.address, phone: d.customer?.phone };

    const warning = d.warning_note;

    const exampleText = 'Образец платежного поручения';

    const bankGrid = {
        table: {
            widths: [270, 120, 120],
            body: [
                [
                    { text: `Бенефициар:\n${bank.beneficiary}\nБИН: ${bank.inn}` },
                    { text: `ИИК\n${bank.iik}`, alignment: 'center' },
                    { text: `Кбе\n${bank.kbe}`, alignment: 'center' },
                ],
                [
                    { text: `Банк бенефициара:\n${bank.bank}` },
                    { text: `БИК\n${bank.bik}`, alignment: 'center' },
                    { text: `Код назначения платежа\n${bank.knp}`, alignment: 'center' },
                ],
            ],
        },
        fontSize: 9,
        margin: [0, 6, 0, 12],
    };

    const supplierBlock = {
        table: {
            widths: [75, '*'],
            body: [
                [{ text: 'Поставщик:', bold: true }, { text: composePartyLine(supplier), margin: [0, 0, 0, 2] }],
                [{ text: 'Покупатель:', bold: true }, { text: composePartyLine(customer), margin: [0, 0, 0, 2] }],
            ],
        },
        layout: borderWhiteRows,
        fontSize: 10,
        margin: [0, 0, 0, 20],
    };

    const tableBody: any[] = [[
        { text: '№', bold: true, alignment: 'center' },
        { text: 'Код', bold: true, alignment: 'center' },
        { text: 'Наименование', bold: true, alignment: 'center' },
        { text: 'Кол-во', bold: true, alignment: 'center' },
        { text: 'Ед.', bold: true, alignment: 'center' },
        { text: 'Цена', bold: true, alignment: 'center' },
        { text: 'Сумма', bold: true, alignment: 'center' },
    ]];

    (d.items ?? []).forEach((it, i) => {
        tableBody.push([
            { text: String(i + 1), alignment: 'center' },
            { text: it.code ?? '' },
            { text: it.title ?? '' },
            { text: it.qty ?? 0, alignment: 'right' },
            { text: it.unit ?? '', alignment: 'center' },
            { text: fmtKZT(it.price ?? 0), alignment: 'right' },
            { text: fmtKZT(it.total ?? 0), alignment: 'right' },
        ]);
    });

    const itemsTable = {
        table: {
            headerRows: 1,
            widths: [20, 70, '*', 50, 50, 50, 50],
            body: tableBody,
        },
        layout: lightRows,
        fontSize: 10,
        margin: [0, 0, 0, 6],
    };

    const totalsTable = {
        table: {
            widths: ['*', 115],
            body: [
                [{ text: 'Итого:', bold: true, alignment: 'right' }, { text: fmtKZT(d.totals?.grand_total ?? 0), alignment: 'right' }],
                ...(d.totals?.vat_included != null
                    ? [[{ text: 'В том числе НДС:', bold: true, alignment: 'right' }, { text: fmtKZT(d.totals.vat_included), alignment: 'right' }]]
                    : []),
            ],
        },
        layout: borderWhiteRows,
        margin: [0, 0, 0, 8],
        fontSize: 10,
    };

    const wordsLine = {
        text:
            `Всего наименований: ${(d.items ?? []).length}, на сумму ${fmtKZT(d.totals?.grand_total ?? 0)},00 KZT\n` +
            (d.totals?.grand_total_words ? `Всего к оплате: ${d.totals.grand_total_words}` : ''),
        margin: [0, 4, 0, 12],
        fontSize: 10,
    };

    const signatures = {
        columns: [
            { text: 'Исполнитель', width: '50%', margin: [0, 30, 0, 0] },
            { text: 'Директор', width: '50%', alignment: 'right', margin: [0, 30, 0, 0] },
        ],
        fontSize: 10,
    };

    return {
        pageSize: 'A4',
        pageMargins: [24, 24, 24, 32],
        content: [
            { text: warning, fontSize: 9, margin: [0, 0, 0, 12] },
            { text: exampleText, fontSize: 10, bold: true, margin: [0, 0, 0, 0] },
            bankGrid,
            { text: `Счет на оплату № ${d.number ?? ''} от ${d.date} г.`, bold: true, fontSize: 14, margin: [0, 20, 0, 20] },
            supplierBlock,
            itemsTable,
            totalsTable,
            wordsLine,
            signatures,
        ].filter(Boolean),
        defaultStyle: { fontSize: 11 },
    };
}

export default function Invoice({data, viewerHeight = 640}: IInvoiceProps) {
    const [url, setUrl] = React.useState<string | null>(null);

    React.useEffect(() => {
        // защита от SSR
        if (typeof window === 'undefined') return;

        let active = true;

        const def: any = buildDocDefinition(data);
        pdfMake.createPdf(def).getBlob((blob: Blob) => {
            if (!active) return;
            const nextUrl = URL.createObjectURL(blob);
            setUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return nextUrl;
            });
        });

        return () => {
            active = false;
            setUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return null;
            });
        };
    }, [data]);

    return (
        <Box>
            {url ? (
                <Box sx={{ border: 0, width: '100%', height: viewerHeight }}>
                    <iframe title='invoice-pdf' src={url} style={{ border: 0, width: '100%', height: '100%' }} />
                </Box>
            ) : (
                <Box p={2}>
                    <Typography color='error'>Не удалось сформировать счёт.</Typography>
                </Box>
            )}
        </Box>
    );
}
